import { useState } from "react";
import { ordersAPI } from "../utils/api";
import {
  FileText,
  Download,
  Printer,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Calendar,
  User,
  MapPin,
  Phone,
  Package,
  Weight,
  Truck,
  Star,
} from "lucide-react";
import toast from "react-hot-toast";

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Invalid Date";
  }
};

const statusConfig = {
  pending: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    label: "Pending",
  },
  assigned: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    label: "Assigned",
  },
  picked_up: {
    color: "bg-violet-100 text-violet-800 border-violet-200",
    label: "Picked Up",
  },
  in_transit: {
    color: "bg-cyan-100 text-cyan-800 border-cyan-200",
    label: "In Transit",
  },
  delivered: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    label: "Delivered",
  },
  failed: {
    color: "bg-rose-100 text-rose-800 border-rose-200",
    label: "Failed",
  },
  cancelled: {
    color: "bg-gray-100 text-gray-800 border-gray-200",
    label: "Cancelled",
  },
};

const InvoiceModal = ({ order, onClose }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  if (!order) return null;

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await ordersAPI.downloadInvoice(order.id);

      // Create blob from response
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Create temporary link and click
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${order.tracking_number}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Invoice downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      toast.error(
        error.response?.data?.message || "Failed to download invoice",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrint = () => {
    setIsPrinting(true);
    window.print();
    setIsPrinting(false);
  };

  const status = statusConfig[order.status] || statusConfig.pending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10 print:hidden">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <FileText className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Invoice</h3>
              <p className="text-sm text-gray-500">{order.tracking_number}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 disabled:opacity-50"
              title="Print"
            >
              <Printer className="w-5 h-5" />
            </button>
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 disabled:opacity-50"
              title="Download PDF"
            >
              {isDownloading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Invoice Content - Printable Area */}
        <div className="p-8 print:p-0" id="invoice-content">
          {/* Company & Invoice Info */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">CourierPro</h2>
              <p className="text-sm text-gray-500 mt-1">
                Professional Courier Services
              </p>
              <p className="text-sm text-gray-500">support@courierpro.com</p>
              <p className="text-sm text-gray-500">+1 234 567 890</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Invoice Date</p>
              <p className="font-semibold text-gray-900">
                {formatDate(order.created_at)}
              </p>
              <p className="text-sm text-gray-500 mt-2">Tracking #</p>
              <p className="font-mono font-semibold text-gray-900">
                {order.tracking_number}
              </p>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mt-3 ${status.color}`}
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                {status.label}
              </span>
            </div>
          </div>

          {/* Route */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-center space-x-2 mb-2">
                <MapPin className="w-4 h-4 text-amber-500" />
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Pickup
                </p>
              </div>
              <p className="text-sm font-medium text-gray-900">
                {order.pickup_address}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-center space-x-2 mb-2">
                <MapPin className="w-4 h-4 text-emerald-500" />
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Delivery
                </p>
              </div>
              <p className="text-sm font-medium text-gray-900">
                {order.delivery_address}
              </p>
              <div className="flex items-center mt-2 text-sm text-gray-600">
                <User className="w-3.5 h-3.5 mr-1" />
                {order.recipient_name}
                <Phone className="w-3.5 h-3.5 ml-3 mr-1" />
                {order.recipient_phone}
              </div>
            </div>
          </div>

          {/* Package Details */}
          <div className="mb-8">
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 pb-2 border-b-2 border-gray-100">
              Package Details
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <Package className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                <p className="text-xs text-gray-500">Type</p>
                <p className="font-semibold text-gray-900 capitalize">
                  {order.package_type}
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <Weight className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                <p className="text-xs text-gray-500">Weight</p>
                <p className="font-semibold text-gray-900">{order.weight} kg</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <Truck className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                <p className="text-xs text-gray-500">Delivery</p>
                <p className="font-semibold text-gray-900 capitalize">
                  {order.delivery_type?.replace("_", " ")}
                </p>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="mb-8">
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 pb-2 border-b-2 border-gray-100">
              Cost Breakdown
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Delivery Fee</span>
                <span className="font-medium">
                  EGP {formatMoney(order.delivery_fee)}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">
                  Weight Charge ({order.weight}kg × EGP 2.00)
                </span>
                <span className="font-medium">
                  EGP {formatMoney(order.weight * 2)}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">
                  EGP {formatMoney(order.subtotal)}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Tax (10%)</span>
                <span className="font-medium">
                  {" "}
                  EGP {formatMoney(order.tax)}
                </span>
              </div>
              <div className="flex justify-between py-3 text-lg font-bold border-t-2 border-gray-200 mt-2">
                <span>Total Amount</span>
                <span className="text-primary-600">
                  EGP {formatMoney(order.total_amount)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Payment Method
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {order.payment_method}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">Amount Paid</p>
                <p className="text-lg font-bold text-emerald-600">
                  EGP {formatMoney(order.total_amount)}
                </p>
              </div>
            </div>
          </div>

          {/* Rating (if delivered) */}
          {order.status === "delivered" && (
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100 mb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="font-semibold text-emerald-900">
                      Your order has been delivered!
                    </p>
                    <p className="text-sm text-emerald-700">
                      How was your delivery experience?
                    </p>
                  </div>
                </div>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star className="w-6 h-6 text-gray-300 hover:text-amber-400" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center text-sm text-gray-500 pt-6 border-t border-gray-200">
            <p className="font-semibold">Thank you for choosing CourierPro!</p>
            <p className="mt-1">
              For support, contact us at support@courierpro.com
            </p>
            <p className="mt-4 text-xs text-gray-400">
              This is a computer-generated invoice and does not require a
              signature.
            </p>
          </div>
        </div>

        {/* Mobile Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex space-x-3 print:hidden">
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="flex-1 btn-primary flex items-center justify-center disabled:opacity-50"
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </>
            )}
          </button>
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
